import { LiveAnnouncer } from "@angular/cdk/a11y";
import { DOCUMENT } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Inject,
    OnDestroy,
    OnInit,
    ViewChild,
} from "@angular/core";
import { distinctUntilChanged, filter, Subscription } from "rxjs";

import { Context, WINDOW } from "@hermes/app-core";
import { VtoBeautyFacade, VtoModalStatus } from "@hermes/states/vto-beauty";

import { calculateCameraDimensions } from "../../helpers/ymk-sdk.helper";

@Component({
    selector: "h-virtual-try-on-camera",
    templateUrl: "./virtual-try-on-camera.component.html",
    styleUrls: ["./virtual-try-on-camera.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VirtualTryOnCameraComponent implements OnInit, OnDestroy {
    @ViewChild("ymkModule")
    public ymkModule!: ElementRef<HTMLElement>;

    private subscription = new Subscription();

    constructor(
        private liveAnnouncer: LiveAnnouncer,
        public vtoBeautyFacade: VtoBeautyFacade,
        private context: Context,
        public changeDetector: ChangeDetectorRef,
        @Inject(WINDOW) public window: Window,
        @Inject(DOCUMENT) public document: Document,
    ) {}

    @HostListener("window:orientationchange")
    public onOrientationChange(): void {
        this.vtoBeautyFacade.changeScreenOrientation();
    }

    @HostListener("window:blur")
    public onIframeFocus(): void {
        const ymkIframe = this.ymkModule?.nativeElement.querySelector(
            "iframe",
        ) as HTMLIFrameElement;
        ymkIframe.tabIndex = -1;
        ymkIframe.ariaHidden = "true";

        // check that window:blur is not fired by another browser interaction
        if (ymkIframe === this.document.activeElement) {
            this.onAir(ymkIframe);
        }
    }

    public ngOnInit(): void {
        // for spinner on start
        this.vtoBeautyFacade.updateCameraDimension(
            calculateCameraDimensions(
                this.window.innerWidth,
                this.window.innerHeight,
            ),
        );

        this.subscription.add(
            this.vtoBeautyFacade.modalStatus$
                .pipe(
                    distinctUntilChanged(),
                    filter(() => this.context.isInBrowserMode()),
                    filter(
                        (modalStatus: VtoModalStatus) =>
                            modalStatus === "CAMERA_LOADED",
                    ),
                )
                .subscribe(() => {
                    this.changeDetector.detectChanges();
                }),
        );
    }

    // a11y tricks
    public onAir(ymkIframe: HTMLIFrameElement): void {
        ymkIframe.title = $localize`:@@hermes.product-page.vto-service:Virtual try-on service | Try different shades on your own face`;

        // without setTimeout, press Escape to close modal does not work
        setTimeout(() => ymkIframe.blur(), 1);

        this.liveAnnouncer.announce(
            $localize`:@@hermes.product-page.vto-product-try-on-title:Products try-on`,
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
